import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { ledgerApi, LedgerEntrySummaryResponse, LedgerEntrySummaryRow } from '../../api';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';

interface LedgerEntrySummaryDialogProps {
  clientId: string;
  accountId: string | null;
  accountName: string | null;
  visible: boolean;
  onHide: () => void;
  onFilterSelect: (status: string) => void;
}

const STATUS_ICONS: Record<string, string> = {
  synced: 'ms-out ms-out-check-circle ms-success',
  ready_to_sync: 'ms-out ms-out-sync ms-info',
  missing_vendor: 'ms-out ms-out-storefront ms-off',
  unallocated: 'ms-out ms-out-link ms-off',
  ignored: 'ms-out ms-out-person ms-off',
};

export const LedgerEntrySummaryDialog: React.FC<LedgerEntrySummaryDialogProps> = ({
  clientId,
  accountId,
  accountName,
  visible,
  onHide,
  onFilterSelect,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<LedgerEntrySummaryResponse | null>(null);

  useEffect(() => {
    if (visible) {
      loadSummary();
    }
  }, [visible, clientId, accountId]);

  const loadSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ledgerApi.getLedgerEntrySummary(clientId, accountId);
      setSummary(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load summary');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterClick = (status: string) => {
    onFilterSelect(status);
    onHide();
  };

  const statusBodyTemplate = (rowData: LedgerEntrySummaryRow) => {
    const icon = STATUS_ICONS[rowData.status] || 'ms-out ms-out-info';
    return (
      <>
        <i className={icon} />
        <span>{rowData.status_label}</span>
        <Button
          icon="ms-out ms-out-filter-list"
          size="small"
          className="filter-btn"
          onClick={() => handleFilterClick(rowData.status)}
          tooltip="Apply this filter"
        />
      </>
    );
  };

  const tableData = summary?.rows || [];
  const totalsRow = summary?.totals;

  const dialogHeader = accountName
    ? `Ledger Entry Summary: ${accountName}`
    : 'Ledger Entry Summary';

  const footerColumnGroup = totalsRow ? (
    <ColumnGroup>
      <Row className="totals-row">
        <Column className="first-column"
          footer={
            <>
              <span>Totals</span>
              <Button
                icon="ms-out ms-out-filter-list"
                size="small"
                className="filter-btn"
                onClick={() => handleFilterClick('all')}
                tooltip="Show all entries"
              />
            </>
          }
        />
        <Column footer={totalsRow.deposits} />
        <Column footer={totalsRow.payments} />
        <Column footer={totalsRow.total} />
      </Row>
    </ColumnGroup>
  ) : undefined;

  const dialogFooter = (
    <div className="dialog-footer">
      <Button label="Close" severity="success" onClick={onHide} />
    </div>
  );

  return (
    <Dialog
      header={dialogHeader}
      visible={visible}
      onHide={onHide}
      className="large-dialog"
      modal
      footer={dialogFooter}
    >
      {error && <Message severity="error" text={error} className="dialog-error-message" />}

      <DataTable
        value={tableData}
        loading={loading}
        dataKey="status"
        size="small"
        stripedRows
        emptyMessage="No entries found"
        footerColumnGroup={footerColumnGroup}
      >
        <Column
          header="Status"
          className="first-column"
          body={statusBodyTemplate}
        />
        <Column
          field="deposits"
          header="Deposits"
        />
        <Column
          field="payments"
          header="Payments"
        />
        <Column
          field="total"
          header="Total"
          className="totals-column"
        />
      </DataTable>
    </Dialog>
  );
};
